/** Shapes more than one resource returns. */
import { z } from 'zod';
import { Cursor, HttpsUrl, IsoDateTime, Limit } from '../common.ts';
import { Channel, DeliveryStatus } from '../enums.ts';
import { DeliveryId, LocationId, StaffId, TenantId, UploadId } from '../ids.ts';

export const API_PREFIX = '/api/v1';
export const TENANT_PREFIX = '/api/v1/t/:t';

export const TenantParams = z.object({ t: TenantId }).strict();

/** `location` narrows any tenant read to one location; omitted means "All locations" (docs/50 §4.12). */
export const LocationQuery = z.object({ location: LocationId.optional() }).strict();

export const PagedLocationQuery = z
  .object({ location: LocationId.optional(), cursor: Cursor.optional(), limit: Limit.optional() })
  .strict();

/** A receipt: something we did, with a link to the live thing (docs/50 P1, docs/52 S21). */
export const LedgerKind = z.enum([
  'gbp_post',
  'gbp_photo',
  'review_reply',
  'site_change',
  'project_page',
  'listing_fix',
  'hours_change',
  'notice',
  'report',
  /** Internal only since deck ruling c144: staff reads stay in the audit log and never reach the client's feed. */
  'staff_view',
  'request_update',
  'other',
  // What the client's own team did (c144): shown in their feed beside what we delivered.
  'team_job',
  'team_review_ask',
  'team_request',
  'team_approval',
]);

export const LedgerItem = z.object({
  id: z.string().max(64),
  at: IsoDateTime,
  kind: LedgerKind,
  title: z.string().min(1).max(160),
  url: HttpsUrl.nullable(),
  /** Who on the client's team did it ("Denise"), for the team_* kinds. Null for what we delivered. */
  actorLabel: z.string().max(80).nullable(),
  locationId: LocationId.nullable(),
});

/** How a notification reached someone (docs/50 U1 lead delivery receipts). */
export const DeliveryRecord = z.object({
  id: DeliveryId,
  channel: Channel,
  status: DeliveryStatus,
  /** "Hank's iPhone", "hank@…" masked, never the full address. */
  recipientLabel: z.string().max(80),
  queuedAt: IsoDateTime,
  sentAt: IsoDateTime.nullable(),
  deliveredAt: IsoDateTime.nullable(),
  openedAt: IsoDateTime.nullable(),
  error: z.string().max(200).nullable(),
});

export const Attachments = z.array(UploadId).max(10);

export const StaffLabel = z.object({ id: StaffId, name: z.string().max(80) });

export const PromisedBy = z.object({
  /** A date, never hours (docs/50 U23). */
  promisedBy: z.iso.date(),
  promisedLabel: z.string().max(60).meta({ examples: ['Tuesday, Oct 7'] }),
});

export const TenantRef = z.object({ id: TenantId, name: z.string().max(60) });
